import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ClassMember, loginClassMember } from "../networking/schoolClasses";
import { getClassroomRoute } from "../helpers/schoolClassHelpers";
import { clearAllPersistedTestData } from "../helpers/testDataStorage";
import {
    clearClassMemberSession,
    clearCodeReminder,
    isClassMemberStorageEvent,
    readClassMemberSession,
    writeClassMemberSession,
} from "./classMemberStorage";

interface ClassMemberContextValue {
    member: ClassMember | null;
    isRestoringSession: boolean;
    /** Class code of a stored session that failed revalidation, used to prefill the login form. */
    revalidatedClassCode: string;
    login: (classCode: string, personalCode: string) => Promise<ClassMember>;
    /**
     * User-initiated logout: also wipes persisted test answers, so a member deliberately leaving
     * a shared device doesn't leave in-progress work behind.
     */
    logout: () => void;
    /**
     * Ends a session that is no longer valid for its context (mismatched class URL on the login page,
     * deleted class) without wiping persisted answers — the owner may still come back,
     * and the rehydrate ownership check withholds them from anyone else.
     */
    discardStaleSession: () => void;
}

const ClassMemberContext = createContext<ClassMemberContextValue | undefined>(undefined);

interface ClassMemberContextProviderProps {
    children: ReactNode;
}

export const ClassMemberContextProvider = (props: ClassMemberContextProviderProps) => {
    const [member, setMember] = useState<ClassMember | null>(null);
    const [isRestoringSession, setIsRestoringSession] = useState(true);
    const [revalidatedClassCode, setRevalidatedClassCode] = useState("");

    const history = useHistory();
    const location = useLocation();
    const locationPathnameRef = useRef(location.pathname);
    locationPathnameRef.current = location.pathname;

    useEffect(() => {
        const stored = readClassMemberSession();
        if (!stored) {
            setIsRestoringSession(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const refreshed = await loginClassMember(stored.member.schoolClassId, stored.loginCode);
                if (cancelled) return;
                writeClassMemberSession(stored.loginCode, refreshed);
                setMember(refreshed);
            } catch {
                if (cancelled) return;
                // No error shown: the member typed nothing, so there is nothing for them to correct.
                // They land on a clean login screen and only see an error if their retry fails too.
                clearClassMemberSession();
                setMember(null);
                setRevalidatedClassCode(stored.member.schoolClassId);
            } finally {
                if (!cancelled) setIsRestoringSession(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const resetRevalidation = useCallback(() => {
        setRevalidatedClassCode("");
    }, []);

    const login = useCallback(
        async (classCode: string, personalCode: string): Promise<ClassMember> => {
            const refreshed = await loginClassMember(classCode, personalCode);
            writeClassMemberSession(personalCode, refreshed);
            setMember(refreshed);
            resetRevalidation();
            return refreshed;
        },
        [resetRevalidation]
    );

    const discardStaleSession = useCallback(() => {
        clearClassMemberSession();
        setMember(null);
        resetRevalidation();
    }, [resetRevalidation]);

    const logout = useCallback(() => {
        clearAllPersistedTestData();
        clearCodeReminder();
        discardStaleSession();
    }, [discardStaleSession]);

    // Cross-tab sync: another tab clearing or rotating the session fires a `storage` event here.
    // Re-read storage and align this tab's React state, and when the active member's class changes,
    // realign the URL so a stale `/klassenzimmer/<oldClass>/...` doesn't keep showing for the new member.
    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (!isClassMemberStorageEvent(e)) return;
            const stored = readClassMemberSession();
            if (!stored) {
                setMember(null);
                resetRevalidation();
                return;
            }
            setMember(stored.member);
            const klassenMatch = locationPathnameRef.current.match(/^\/klassenzimmer\/([^/]+)/);
            if (klassenMatch && klassenMatch[1].toUpperCase() !== stored.member.schoolClassId.toUpperCase()) {
                history.replace(getClassroomRoute(stored.member.schoolClassId));
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [history, resetRevalidation]);

    const value: ClassMemberContextValue = {
        member,
        isRestoringSession,
        revalidatedClassCode,
        login,
        logout,
        discardStaleSession,
    };

    return <ClassMemberContext.Provider value={value}>{props.children}</ClassMemberContext.Provider>;
};

export const useClassMember = (): ClassMemberContextValue => {
    const ctx = useContext(ClassMemberContext);
    if (!ctx) {
        throw new Error("useClassMember must be used inside ClassMemberContextProvider");
    }
    return ctx;
};
